import dagre from "@dagrejs/dagre";
import {
	Background,
	Controls,
	type Edge,
	Handle,
	MarkerType,
	type Node,
	type NodeProps,
	Position,
	ReactFlow,
	type ReactFlowProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { memo, useMemo } from "react";
import type {
	StatusDisplay2,
	StatusLifecycleDisplay,
	StatusTransitionDisplay,
} from "@/services/lifecycle/lifecycle.types";
import { resolveIconComponent } from "@/utils/icon-catalog";
import {
	getLifecycleStatuses,
	getLifecycleTransitions,
} from "@/utils/lifecycle-detail/lifecycle-detail.utils";
import { resolveStatusCategoryColors } from "@/utils/status-category-colors";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatusNodeData = {
	label: string;
	iconColor?: string | null;
	icon?: string | null;
};

type StatusNodeProps = NodeProps<Node<StatusNodeData>>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NODE_WIDTH = 160;
const NODE_HEIGHT = 64;

// Invisible handles — connection points only, not visible in the UI
const invisibleHandle: React.CSSProperties = {
	background: "transparent",
	border: "none",
	width: 4,
	height: 4,
};

// ---------------------------------------------------------------------------
// Layout helper
// ---------------------------------------------------------------------------

function layoutGraph(nodes: Node<StatusNodeData>[], edges: Edge[]) {
	const g = new dagre.graphlib.Graph();
	g.setDefaultEdgeLabel(() => ({}));
	g.setGraph({ rankdir: "LR", nodesep: 40, ranksep: 80 });

	for (const n of nodes) {
		g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
	}
	for (const e of edges) {
		g.setEdge(e.source, e.target);
	}

	dagre.layout(g);

	return nodes.map((n) => {
		const pos = g.node(n.id);
		return {
			...n,
			position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
		};
	});
}

// ---------------------------------------------------------------------------
// Main-path detection (longest simple path from initial → determines solid edges)
// ---------------------------------------------------------------------------

function findMainPathEdgeIds(
	initialId: string | undefined,
	transitions: StatusTransitionDisplay[],
): Set<string> {
	if (!initialId) return new Set();

	const adj = new Map<string, string[]>();
	for (const t of transitions) {
		const from = t.fromStatus?.id;
		const to = t.toStatus?.id;
		if (from && to) {
			if (!adj.has(from)) adj.set(from, []);
			(adj.get(from) as string[]).push(to);
		}
	}

	let longest: string[] = [initialId];

	function dfs(node: string, path: string[], seen: Set<string>) {
		if (path.length > longest.length) longest = [...path];
		for (const next of adj.get(node) ?? []) {
			if (!seen.has(next)) {
				seen.add(next);
				dfs(next, [...path, next], seen);
				seen.delete(next);
			}
		}
	}

	dfs(initialId, [initialId], new Set([initialId]));

	const ids = new Set<string>();
	for (let i = 0; i < longest.length - 1; i++) {
		ids.add(`${longest[i]}->${longest[i + 1]}`);
	}
	return ids;
}

// ---------------------------------------------------------------------------
// Custom node component
// ---------------------------------------------------------------------------

const StatusNode = memo(({ data }: StatusNodeProps) => {
	const { color: borderColor, bgColor: badgeBg } = resolveStatusCategoryColors(
		data.iconColor,
	);
	const IconComponent = resolveIconComponent(data.icon);

	return (
		<div
			className="relative flex flex-col rounded-lg border-2 bg-white px-3 py-2.5 shadow-sm"
			style={{ width: NODE_WIDTH, borderColor }}
		>
			{/* Target handle — left edge */}
			<Handle type="target" position={Position.Left} style={invisibleHandle} />

			<p className="mb-1.5 truncate text-[13px] font-semibold leading-tight text-gray-900">
				{data.label}
			</p>

			<span
				className="inline-flex w-fit max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
				style={{ backgroundColor: badgeBg, borderColor, color: borderColor }}
			>
				{IconComponent ? (
					<IconComponent className="size-3 shrink-0" />
				) : (
					<span
						className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
						style={{ backgroundColor: borderColor }}
					/>
				)}
				<span className="truncate">{data.label}</span>
			</span>

			{/* Source handle — right edge */}
			<Handle type="source" position={Position.Right} style={invisibleHandle} />
		</div>
	);
});
StatusNode.displayName = "StatusNode";

const nodeTypes = { statusNode: StatusNode };

// ---------------------------------------------------------------------------
// Build nodes + edges from lifecycle data
// ---------------------------------------------------------------------------

function buildNodesAndEdges(lifecycle: StatusLifecycleDisplay): {
	nodes: Node<StatusNodeData>[];
	edges: Edge[];
} {
	const allStatuses: StatusDisplay2[] = getLifecycleStatuses(lifecycle);
	const transitions = getLifecycleTransitions(lifecycle);
	const initialId = lifecycle.initialStatus?.id;
	const mainPathIds = findMainPathEdgeIds(initialId, transitions);

	const nodes: Node<StatusNodeData>[] = allStatuses.map((s) => ({
		id: s.id,
		type: "statusNode",
		position: { x: 0, y: 0 },
		data: {
			label: s.name ?? s.id,
			iconColor:
				s.statusCategoryIconColor ?? s.statusCategory?.iconColor ?? null,
			icon: s.statusCategoryIcon ?? s.statusCategory?.icon ?? null,
		},
	}));

	const edges: Edge[] = transitions
		.filter((t) => t.fromStatus?.id && t.toStatus?.id)
		.map((t) => {
			const fromId = t.fromStatus?.id as string;
			const toId = t.toStatus?.id as string;
			const edgeId = `${fromId}->${toId}`;
			const isMain = mainPathIds.has(edgeId);
			const { color } = resolveStatusCategoryColors(
				t.fromStatus?.statusCategoryIconColor ??
					t.fromStatus?.statusCategory?.iconColor,
			);

			return {
				id: edgeId,
				source: fromId,
				target: toId,
				type: "smoothstep",
				style: isMain
					? { stroke: "#374151", strokeWidth: 2 }
					: {
							strokeDasharray: "5 4",
							stroke: color,
							strokeWidth: 1.5,
						},
				markerEnd: {
					type: MarkerType.ArrowClosed,
					color: isMain ? "#374151" : color,
					width: 16,
					height: 16,
				},
			};
		});

	return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface LifecycleFlowVisualizationProps {
	lifecycle: StatusLifecycleDisplay;
	height?: number | string;
}

const flowProps: Partial<ReactFlowProps> = {
	nodesDraggable: false,
	nodesConnectable: false,
	elementsSelectable: false,
	panOnDrag: true,
	zoomOnScroll: true,
	fitView: true,
	fitViewOptions: { padding: 0.3 },
	minZoom: 0.2,
	maxZoom: 2,
	proOptions: { hideAttribution: false },
};

export function LifecycleFlowVisualization({
	lifecycle,
	height = "100%",
}: LifecycleFlowVisualizationProps) {
	const { nodes: layoutedNodes, edges } = useMemo(() => {
		const { nodes, edges } = buildNodesAndEdges(lifecycle);
		const layoutedNodes = layoutGraph(nodes, edges);
		return { nodes: layoutedNodes, edges };
	}, [lifecycle]);

	const resolvedHeight =
		typeof height === "number" ? `${height}px` : height;

	return (
		<div style={{ height: resolvedHeight }} className="w-full overflow-hidden rounded-lg">
			<ReactFlow
				nodes={layoutedNodes}
				edges={edges}
				nodeTypes={nodeTypes}
				{...flowProps}
			>
				<Background gap={16} size={1} color="#e5e7eb" />
				<Controls showInteractive={false} />
			</ReactFlow>
		</div>
	);
}
